import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, query } from "../../lib/api";
import { formatAmount, formatDate, titleCase } from "../../lib/format";
import type { AdminTenant, Balance, ListResponse, PricingPlan } from "../../lib/types";
import {
  Button,
  EmptyState,
  ErrorNotice,
  Modal,
  Mono,
  PageHeader,
  Pagination,
  TextAreaField,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

const PAGE_SIZE = 25;

export function Tenants() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);

  const tenants = useQuery({
    queryKey: ["admin-tenants", applied, status, offset],
    queryFn: () =>
      api.get<ListResponse<AdminTenant>>(
        `/api/admin/tenants${query({ search: applied, status, limit: PAGE_SIZE, offset })}`,
      ),
  });

  const rows = tenants.data?.data ?? [];

  return (
    <>
      <PageHeader eyebrow="Operations" title="Client firms" description="Every business registered on PayFlow." />

      <div className="settle settle-1 mb-4 flex flex-wrap gap-2">
        <form
          className="flex min-w-[220px] flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied(search.trim());
            setOffset(0);
          }}
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            aria-label="Search client firms"
            className="w-full rounded-md border border-rule-strong bg-paper-raised px-3 py-1.5 text-[13px] placeholder:text-ink-faint focus:border-verdigris-bright focus:outline-none focus:ring-2 focus:ring-verdigris-bright/15"
          />
        </form>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
          aria-label="Filter by status"
          className="rounded-md border border-rule-strong bg-paper-raised px-2.5 py-1.5 text-[13px]"
        >
          <option value="">All statuses</option>
          {["pending_kyc", "kyc_submitted", "kyc_in_review", "approved", "rejected", "suspended"].map((s) => (
            <option key={s} value={s}>
              {titleCase(s)}
            </option>
          ))}
        </select>
      </div>

      <Sheet className="settle settle-2 overflow-hidden">
        {tenants.isLoading ? (
          <Skeleton />
        ) : rows.length === 0 ? (
          <EmptyState title="No firms match" description="Try clearing the filters." />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Firm</Th>
                  <Th>Status</Th>
                  <Th>Contact</Th>
                  <Th>Payouts</Th>
                  <Th align="right">Registered</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((tenant) => (
                  <Tr key={tenant.id} onClick={() => navigate(`/admin/tenants/${tenant.id}`)}>
                    <Td>
                      <span className="text-[13.5px] text-ink">{tenant.legal_name}</span>
                      {tenant.trading_name && tenant.trading_name !== tenant.legal_name && (
                        <span className="mt-0.5 block text-[11.5px] text-ink-faint">
                          trading as {tenant.trading_name}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <StatusPill status={tenant.status} />
                    </Td>
                    <Td className="text-[12.5px] text-ink-muted">{tenant.contact_email}</Td>
                    <Td className="text-[12.5px] text-ink-muted">
                      {titleCase(tenant.payout_schedule)}
                    </Td>
                    <Td align="right" className="whitespace-nowrap text-[12px] text-ink-muted">
                      {formatDate(tenant.created_at)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={tenants.data?.total ?? 0}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Sheet>
    </>
  );
}

/* -------------------------------------------------------------------------- */

interface TenantDetailResponse {
  tenant: AdminTenant;
  balances: Balance[] | null;
  pricing: PricingPlan;
}

export function TenantDetail() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const [suspending, setSuspending] = useState(false);
  const [reason, setReason] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [planId, setPlanId] = useState("");
  const [payingOut, setPayingOut] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["admin-tenant", id],
    queryFn: () => api.get<TenantDetailResponse>(`/api/admin/tenants/${id}`),
  });

  const plans = useQuery({
    queryKey: ["admin-plans"],
    queryFn: () => api.get<ListResponse<PricingPlan>>("/api/admin/pricing-plans"),
    enabled: assigning,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-tenant", id] });
    queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
  };

  const suspend = useMutation({
    mutationFn: () => api.post(`/api/admin/tenants/${id}/suspend`, { reason }),
    onSuccess: () => {
      setSuspending(false);
      setReason("");
      invalidate();
    },
  });

  const reinstate = useMutation({
    mutationFn: () => api.post(`/api/admin/tenants/${id}/reinstate`),
    onSuccess: invalidate,
  });

  const assignPlan = useMutation({
    mutationFn: () => api.post(`/api/admin/tenants/${id}/pricing-plan`, { plan_id: planId }),
    onSuccess: () => {
      setAssigning(false);
      invalidate();
    },
  });

  const createPayout = useMutation({
    mutationFn: (currency: string) => api.post("/api/admin/payouts", { tenant_id: id, currency }),
    onSuccess: () => {
      setPayingOut(null);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["admin-payouts"] });
    },
  });

  if (detail.isLoading) {
    return (
      <Sheet>
        <Skeleton rows={8} />
      </Sheet>
    );
  }
  if (detail.error) return <ErrorNotice error={detail.error} />;

  const { tenant, balances, pricing } = detail.data!;
  const available = balances ?? [];

  return (
    <>
      <Link
        to="/admin/tenants"
        className="mb-4 inline-flex items-center gap-1.5 text-[12.5px] text-ink-muted transition-colors hover:text-ink"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M7.5 2.5 4 6l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        All client firms
      </Link>

      <PageHeader
        eyebrow={tenant.id}
        title={tenant.legal_name}
        description={tenant.contact_email}
        actions={
          <div className="flex items-center gap-2">
            <StatusPill status={tenant.status} />
            {tenant.status === "suspended" ? (
              <Button loading={reinstate.isPending} onClick={() => reinstate.mutate()}>
                Reinstate
              </Button>
            ) : (
              tenant.status === "approved" && (
                <Button variant="danger" onClick={() => setSuspending(true)}>
                  Suspend
                </Button>
              )
            )}
          </div>
        }
      />

      {tenant.suspended_reason && (
        <div className="settle mb-6 rounded-md border border-sienna/25 bg-sienna-wash px-4 py-3">
          <p className="label mb-1">Suspended</p>
          <p className="text-[13px] text-ink">{tenant.suspended_reason}</p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Sheet className="settle settle-1 overflow-hidden">
          <SheetHeader title="Owed to this firm" description="Cleared and awaiting payout" />
          {available.length === 0 ? (
            <p className="px-5 py-8 text-center text-[13px] text-ink-muted">
              Nothing outstanding.
            </p>
          ) : (
            <ul className="divide-y divide-rule">
              {available.map((balance) => (
                <li key={balance.currency} className="flex items-center justify-between px-5 py-3.5">
                  <div>
                    <span className="font-mono text-[12px] text-ink-muted">
                      {balance.currency}
                    </span>
                    <span className="ml-3 font-display text-[20px] tabular">
                      {formatAmount(balance.minor)}
                    </span>
                  </div>
                  {balance.minor > 0 && (
                    <Button size="sm" onClick={() => setPayingOut(balance.currency)}>
                      Pay out
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Sheet>

        <Sheet className="settle settle-2 overflow-hidden">
          <SheetHeader
            title="Pricing"
            description={pricing.name}
            actions={
              <Button size="sm" onClick={() => setAssigning(true)}>
                Change plan
              </Button>
            }
          />
          <ul className="divide-y divide-rule">
            {pricing.rules.slice(0, 6).map((rule) => (
              <li key={rule.id} className="px-5 py-2.5 text-[12.5px] text-ink-muted">
                {rule.summary}
              </li>
            ))}
          </ul>
        </Sheet>

        <Sheet className="settle settle-3 overflow-hidden lg:col-span-2">
          <SheetHeader title="Account" />
          <dl className="divide-y divide-rule text-[13px]">
            <Row label="Tenant ID" value={<Mono copyable>{tenant.id}</Mono>} />
            <Row label="Registered" value={formatDate(tenant.created_at)} />
            <Row label="Approved" value={tenant.approved_at ? formatDate(tenant.approved_at) : "Not yet"} />
            <Row label="Payout schedule" value={titleCase(tenant.payout_schedule)} />
          </dl>
        </Sheet>
      </div>

      <Modal
        open={suspending}
        onClose={() => setSuspending(false)}
        title="Suspend this firm?"
        description="Live API keys are revoked immediately and they cannot take further payments."
        footer={
          <>
            <Button onClick={() => setSuspending(false)}>Cancel</Button>
            <Button
              variant="danger"
              loading={suspend.isPending}
              disabled={reason.trim() === ""}
              onClick={() => suspend.mutate()}
            >
              Suspend
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <ErrorNotice error={suspend.error} />
          <TextAreaField
            label="Reason (recorded in the audit log)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
          />
        </div>
      </Modal>

      <Modal
        open={assigning}
        onClose={() => setAssigning(false)}
        title="Change pricing plan"
        description="The new plan applies to payments created from now on. Existing payments keep the fee they were charged."
        footer={
          <>
            <Button onClick={() => setAssigning(false)}>Cancel</Button>
            <Button variant="primary" loading={assignPlan.isPending} onClick={() => assignPlan.mutate()}>
              Assign plan
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <ErrorNotice error={assignPlan.error} />
          <select
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            className="w-full rounded-md border border-rule-strong bg-paper-raised px-3 py-2 text-[13.5px]"
          >
            <option value="">Platform default</option>
            {(plans.data?.data ?? []).map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
                {plan.is_default ? " (default)" : ""}
              </option>
            ))}
          </select>
        </div>
      </Modal>

      <Modal
        open={payingOut !== null}
        onClose={() => setPayingOut(null)}
        title="Create a payout"
        description="This gathers every cleared payment in this currency into one settlement and moves the funds out of the firm's available balance."
        footer={
          <>
            <Button onClick={() => setPayingOut(null)}>Cancel</Button>
            <Button
              variant="primary"
              loading={createPayout.isPending}
              onClick={() => payingOut && createPayout.mutate(payingOut)}
            >
              Create payout
            </Button>
          </>
        }
      >
        <ErrorNotice error={createPayout.error} />
        <p className="mt-2 text-[13px] text-ink-muted">
          Only payments that have cleared their settlement hold are included. You will then instruct the
          transfer at the bank and confirm it here with the bank reference.
        </p>
      </Modal>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-2.5">
      <dt className="shrink-0 text-ink-muted">{label}</dt>
      <dd className="min-w-0 text-right text-ink">{value}</dd>
    </div>
  );
}
